import { format } from "date-fns";
import { createContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import AlertWrapper from "~/src/components/Alerts/AlertWrapper";
import BottomBar from "~/src/components/BottomBars/BottomBars";
import BottomLine from "~/src/components/BottomLine";
import { dateFormatNorsk } from "~/src/components/CalendarPicker/CalendarConstants";
import { SaveIcon } from "~/src/components/Icons/Icons";
import { FormDataContext } from "~/src/contexts/FormDataContext/FormDataContext";
import { RegistrerJaktdagProps } from "~/src/pages/Registrering/Jaktdag/common/types";
import { AccordionDefault } from "../../../../components/Accordion/AccordionDefault";
import AlertDefault, { AlertVariant } from "../../../../components/Alerts/AlertDefault";
import ButtonDefault, { ButtonVariant } from "../../../../components/Button/ButtonDefault";
import CalendarPicker from "../../../../components/CalendarPicker/CalendarPicker";
import Card from "../../../../components/Card/Card";
import Counter from "../../../../components/Counter/Counter";
import JaktomradePicker from "../../../../components/JaktomradePicker/JaktomradePicker";
import { ArtType } from "../../../../helpers/types";
import AntallJegereOgTimerJaktet from "../common/AntallJegereOgTimerJaktet";
import { JaktdagRegistrationDataElg } from "./ElgTypes";
import SettCountersElg from "./SettCounters";
import SkuttCountersElg from "./SkuttCounters";
import useRegistrerElg from "./useRegistrerElg";

export const JaktdagElgFormDataContext = createContext<
  FormDataContext<JaktdagRegistrationDataElg> | undefined
>(undefined);

export default function RegistrerElg({ initialData }: RegistrerJaktdagProps): JSX.Element {
  const scrollTopRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const {
    handleSubmit,
    isSubmitting,
    formData,
    formErrors,
    setFormData,
    setFormErrors,
    fellingsRapportLevert,
    date,
    selectedJaktomrade,
    eksisterendeJaktdag,
    loadingEksisterendeJaktdager,
    jaktdagEksistererDialog,
    successDialog,
    handleChangeJaktomrade,
    settOgSkuttData,
    settErrorMessage,
    skuttErrorMessage,
    hasGodkjenteDyr,
  } = useRegistrerElg(initialData, scrollTopRef);

  return (
    <JaktdagElgFormDataContext.Provider
      value={{ formData, formErrors, setFormData, setFormErrors: setFormErrors }}
    >
      <div ref={scrollTopRef} />
      <form className="h-full flex flex-col justify-between pt-4">
        <AlertWrapper className="mx-4">
          {fellingsRapportLevert.value && (
            <AlertDefault id="registreringFellingsrapportLevertAlert" variant={AlertVariant.Info}>
              {t("pages.registrering.alerts.fellingsrapportLevert")}
            </AlertDefault>
          )}
          {formErrors.Id && (
            <AlertDefault variant={AlertVariant.Error}>{formErrors.Id}</AlertDefault>
          )}
          {!!eksisterendeJaktdag.value && (
            <AlertDefault variant={AlertVariant.Warning}>
              {t("pages.registrering.errors.jaktdagEksisterer")}
            </AlertDefault>
          )}
        </AlertWrapper>

        <Card color="transparent" shadow={false} className="mx-4">
          <AccordionDefault
            title={t("pages.registrerArt.datoOgJaktfelt")}
            details={
              formData.jaktomradeNavn &&
              date.value &&
              format(date.value, dateFormatNorsk) + " " + formData.jaktomradeNavn
            }
            error={Boolean(
              formErrors.Jaktfelt || formErrors.AntallJegere || formErrors.AntallTimerJaktet,
            )}
            info={
              hasGodkjenteDyr ? t("pages.registrerArt.kanIkkeEndreDatoOgJaktomrade") : undefined
            }
          >
            <CalendarPicker
              id="registrerArtDatePicker"
              label={hasGodkjenteDyr ? t("pages.registrerArt.dato") : t("form.fields.date.label")}
              date={date}
              readOnly={fellingsRapportLevert.value || hasGodkjenteDyr}
              errorText={formErrors.Dato}
            />
            {!(initialData === undefined) && <BottomLine className="mb-2" />}
            <JaktomradePicker
              className="mt-4 mb-8"
              selectedState={selectedJaktomrade}
              valgtArt={ArtType.Elg}
              onChange={(selected) => handleChangeJaktomrade(selected)}
              readOnly={fellingsRapportLevert?.value || hasGodkjenteDyr}
              defaultOmradekode={formData.Jaktfelt}
              helperText={
                initialData === undefined ? t("form.fields.jaktfelt.helperText") : undefined
              }
              errorText={formErrors.Jaktfelt}
            />
            <AntallJegereOgTimerJaktet
              formData={formData}
              setFormData={setFormData as (data: JaktdagRegistrationDataElg) => void}
              errors={formErrors}
              setErrors={setFormErrors}
              disabled={fellingsRapportLevert.value}
            />
            <BottomLine className="mt-4" />
          </AccordionDefault>
          <AccordionDefault
            count={settOgSkuttData.sumSett}
            title={t("pages.registrerArt.sett")}
            disabled={fellingsRapportLevert.value}
            errorMessage={settErrorMessage}
            info={
              !settErrorMessage
                ? t("pages.registrerArt.settHelpText", {
                    art: t("fauna.art.elg.artsnavn").toLowerCase(),
                  })
                : undefined
            }
            useErrorIcon
          >
            <SettCountersElg />
            <Counter
              showErrorHelperText={false}
              id="settSumKalver"
              label="Sum kalver"
              showButtons={false}
              value={settOgSkuttData.settKalver}
              readOnly={true}
            />
            <BottomLine />
          </AccordionDefault>
          <AccordionDefault
            count={settOgSkuttData.sumSkutt}
            title={t("pages.registrerArt.skutt")}
            disabled={fellingsRapportLevert.value}
            errorMessage={skuttErrorMessage}
            info={!skuttErrorMessage ? t("form.fields.skuttAntall.validationError") : undefined}
            useErrorIcon
          >
            <SkuttCountersElg />
          </AccordionDefault>
          {successDialog.element}
          {jaktdagEksistererDialog.element}
        </Card>

        <BottomBar>
          <ButtonDefault
            id="registrerElgLagreButton"
            label={t("actions.save")}
            icon={<SaveIcon />}
            variant={ButtonVariant.PrimaryDarkBg}
            type="button"
            loading={isSubmitting}
            onClick={handleSubmit}
            disabled={
              fellingsRapportLevert.value ||
              !!eksisterendeJaktdag.value ||
              loadingEksisterendeJaktdager ||
              isSubmitting
            }
          />
        </BottomBar>
      </form>
    </JaktdagElgFormDataContext.Provider>
  );
}
